import { useTranslation } from "react-i18next";
import { Button, Drawer, Toast } from "@/ui-kit";
import { Form } from "@/components/controls";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import type { IInspector } from "@/types/users-inspectors";
import type { IAddress } from "@/types/address";
import { AddressControl } from "@/components/controls/AddressControl";
import { addressSchema } from "@/utils/validation";
import { AddressUtils } from "@/utils/address";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IProps = {
  inspector: IInspector;
  onSave: (updatedInspector: IInspector) => void;
  onDismiss: () => void;
};

type IInspectorDetailsFormData = {
  address: IAddress;
};

const EditModal = ({ inspector, onSave, onDismiss }: IProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const schema = useMemo(() => {
    return z.object({
      address: addressSchema,
    });
  }, []);

  const form = useForm<IInspectorDetailsFormData>({
    defaultValues: {
      address: AddressUtils.getNormalized(inspector.address),
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const repository = useMemo(() => {
    return new InspectorsRepository(new InspectorsService(), { abort: true });
  }, []);

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IInspectorDetailsFormData) => {
      setLoading(true);

      repository
        .updateInspector({
          id: inspector.userId,
          userId: inspector.userId,
          address: formData.address,
        })
        .then((response) => {
          onSave(response);
          Toast.success({
            title: t("toast.what_updated", {
              what: t("label.address"),
            }),
          });
          onDismiss();
        })
        .catch(
          serverErrorHandler(() => {
            setLoading(false);
          }),
        );
    })();
  }, [handleSubmit, repository, inspector.userId, onSave, onDismiss, t]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  return (
    <FormProvider {...form}>
      <Drawer
        open={true}
        onOpenChange={onDismiss}
        title={t("label.address")}
        closeOnOutsidePress={false}
        footer={
          <>
            <Button onClick={handleDismiss} variant="ghost" color="layer1">
              {t("action.cancel")}
            </Button>
            <Button onClick={onSubmit} loading={loading}>
              {t("action.save")}
            </Button>
          </>
        }
      >
        <Form>
          <AddressControl label={t("label.address")} name="address" />
        </Form>
      </Drawer>
    </FormProvider>
  );
};

export default EditModal;
